// src/cockpit4/components/AILauncherPanel.tsx
// ============================================
// AI 启动器面板 — XEAOS AI 工作台核心入口
//
// 功能：
//   1. 快速启动本地应用（Obsidian 大脑、微信、浏览器等）
//   2. 快速跳转到 XEAOS 各工作区
//   3. 快速访问外部产品（量化、Marketing OS 等）
//   4. 一键执行常用 AI 操作（生成晨报、内容生产、知识查询）
//
// 设计：Bloomberg Terminal + Raycast 风格
// ============================================

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, MessageCircle, Globe, Factory, Crown, Megaphone,
  Users, BookOpen, Store, Wallet, Sparkles, Crosshair,
  Settings, Rocket, TrendingUp, FileText, Search, Zap,
  ExternalLink, Command, ArrowRight, Radar, type LucideIcon,
} from "lucide-react";
import { useCockpit4Store, type WorkspaceId } from "../store";
// v6.6：角色门控（超级管理员可见受限产品，对外不展示量化/国学）
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义
// ============================================

interface LaunchItem {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
  category: "brain" | "app" | "workspace" | "product" | "action";
  action: () => void;
  shortcut?: string;
  external?: boolean;
}

// ============================================
// 本地应用启动配置
// ============================================

const LOCAL_APPS = [
  {
    id: "obsidian",
    label: "Obsidian 大脑",
    description: "打开企业知识库中央大脑",
    icon: Brain,
    color: "#A855F7",
    command: "obsidian://open",
    path: "D:/Program Files/Obsidian/Obsidian.exe",
  },
  {
    id: "wechat",
    label: "微信",
    description: "启动微信客户端",
    icon: MessageCircle,
    color: "#22C55E",
    command: "weixin://",
    path: "",
  },
  {
    id: "browser",
    label: "浏览器",
    description: "打开默认浏览器",
    icon: Globe,
    color: "#3B82F6",
    command: "https://os.xshetech.com",
    path: "",
  },
];

// ============================================
// 外部产品
// ============================================

const EXTERNAL_PRODUCTS = [
  {
    id: "xeaos",
    label: "XEAOS 主系统",
    url: "https://os.xshetech.com",
    icon: Rocket,
    color: "#6366F1",
    description: "企业 AI 操作系统",
  },
  {
    id: "quant",
    label: "量化交易台",
    url: "https://trade.xshetech.com",
    icon: TrendingUp,
    color: "#F472B6",
    description: "黄金外汇 / 股票期权",
  },
  {
    id: "xuanji",
    label: "AI Destiny",
    url: "https://xuanji.xshetech.com",
    icon: Sparkles,
    color: "#FBBF24",
    description: "AI 玄机决策系统",
  },
];

// ============================================
// 工作区配置
// ============================================

const WORKSPACE_TABS: Array<{ id: WorkspaceId; label: string; icon: LucideIcon; color: string; description: string }> = [
  { id: "ceo", label: "总控台", icon: Crown, color: "#F59E0B", description: "CEO 驾驶舱 · 晨报/KPI/风险" },
  { id: "marketing", label: "营销中心", icon: Megaphone, color: "#EC4899", description: "营销漏斗 · ROI · 分发" },
  { id: "media_studio", label: "内容工厂", icon: Factory, color: "#A855F7", description: "AI 内容生产 · 图片/视频/音乐" },
  { id: "crm", label: "客户中心", icon: Users, color: "#3B82F6", description: "CRM · 客户360 · 跟进" },
  { id: "knowledge", label: "知识中心", icon: BookOpen, color: "#22D3EE", description: "知识图谱 · QA · 飞轮" },
  { id: "projects", label: "AI 超市", icon: Store, color: "#A855F7", description: "AI 产品货架 · 量化 · 产品" },
  { id: "finance", label: "财务中心", icon: Wallet, color: "#10B981", description: "财务三表 · 预算 · 成本" },
  { id: "workflow", label: "经营中心", icon: Crosshair, color: "#06B6D4", description: "目标驱动 · SOP · GEO+DSO" },
  { id: "settings", label: "设置", icon: Settings, color: "#9CA3AF", description: "系统配置 · API · RBAC" },
];

// ============================================
// 主组件
// ============================================

export default function AILauncherPanel({ onClose }: { onClose?: () => void }) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const toggleHermesDrawer = useCockpit4Store((s) => s.toggleHermesDrawer);
  // v6.6：角色门控——超级管理员（元）可见量化/国学启动项；员工/游客隐藏
  const currentUser = useUserStore((s) => s.currentUser);
  const isSuperAdmin = currentUser?.role === "super_admin";
  // v6.24 角色门控——游客仅 AI 超市；员工隐藏财务中心
  const isGuest = currentUser?.role === "guest";
  const isEmployee = currentUser?.role === "employee";

  // 快捷键：Esc 关闭
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // 启动本地应用
  const launchApp = (app: typeof LOCAL_APPS[0]) => {
    // 浏览器环境：通过 window.open 或自定义协议启动
    if (app.command.startsWith("http")) {
      window.open(app.command, "_blank");
    } else {
      // 自定义协议（obsidian://, weixin:// 等）
      window.location.href = app.command;
    }
  };

  // 跳转工作区
  const goToWorkspace = (ws: WorkspaceId) => {
    setWorkspace(ws);
    if (onClose) onClose();
  };

  // 打开外部产品
  const openProduct = (url: string) => {
    window.open(url, "_blank");
  };

  // 构建所有启动项
  const allItems: LaunchItem[] = [
    // 本地应用
    ...LOCAL_APPS.map((app) => ({
      id: app.id,
      label: app.label,
      description: app.description,
      icon: app.icon,
      color: app.color,
      category: "brain" as const,
      action: () => launchApp(app),
      shortcut: app.id === "obsidian" ? "⌘O" : undefined,
    })),
    // 外部产品（v6.6：量化/国学仅超级管理员可见）
    ...EXTERNAL_PRODUCTS
      .filter((p) => isSuperAdmin || (p.id !== "quant" && p.id !== "xuanji"))
      .map((p) => ({
      id: p.id,
      label: p.label,
      description: p.description,
      icon: p.icon,
      color: p.color,
      category: "product" as const,
      action: () => openProduct(p.url),
      external: true,
    })),
    // 工作区（v6.39 游客全览：不再过滤——全部工作区可预览，数据由渲染层遮罩/加密锁保护；员工隐藏财务中心）
    ...WORKSPACE_TABS
      .filter((ws) => {
        if (isEmployee) return ws.id !== "finance";
        return true;
      })
      .map((ws) => ({
      id: `ws-${ws.id}`,
      label: ws.label,
      description: ws.description,
      icon: ws.icon,
      color: ws.color,
      category: "workspace" as const,
      action: () => goToWorkspace(ws.id),
    })),
    // 快速操作（v6.24 游客隐藏——避免引导进入付费/内部功能）
    ...(isGuest ? [] : [
      {
        id: "action-morning-report",
        label: "生成今日晨报",
        description: "AI 自动生成企业晨报并写入 Obsidian",
        icon: FileText,
        color: "#F59E0B",
        category: "action" as const,
        action: () => { goToWorkspace("ceo"); },
      },
      {
        id: "action-content",
        label: "内容生产",
        description: "AI 生成图文/视频/音乐内容包",
        icon: Factory,
        color: "#A855F7",
        category: "action" as const,
        action: () => { goToWorkspace("media_studio"); },
      },
      {
        id: "action-knowledge",
        label: "知识查询",
        description: "向企业大脑提问",
        icon: Search,
        color: "#22D3EE",
        category: "action" as const,
        action: () => { goToWorkspace("knowledge"); },
      },
      {
        id: "action-hermes",
        label: "Hermes 对话",
        description: "呼出 AI 助手对话",
        icon: Sparkles,
        color: "#8B5CF6",
        category: "action" as const,
        action: () => { toggleHermesDrawer(); },
      },
      {
        id: "action-ai-visibility",
        label: "AI 可见性监测",
        description: "品牌在豆包/Kimi/DeepSeek 的提及率与首推率",
        icon: Radar,
        color: "#22D3EE",
        category: "action" as const,
        action: () => {
          goToWorkspace("workflow");
          // 带 URL 参数直达 AI 可见性 Tab（WorkflowWorkspace 初始化时解析）
          history.replaceState(null, "", "?tab=aivisibility");
          window.dispatchEvent(new PopStateEvent("popstate"));
        },
      },
    ]),
  ];

  // 过滤
  const filtered = allItems.filter((item) => {
    const matchCategory = selectedCategory === "all" || item.category === selectedCategory;
    const matchSearch = !search ||
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  const categories = [
    { id: "all", label: "全部", icon: Command },
    { id: "brain", label: "大脑/应用", icon: Brain },
    { id: "workspace", label: "工作区", icon: LayoutGrid },
    { id: "product", label: "产品", icon: Rocket },
    { id: "action", label: "AI 操作", icon: Zap },
  ];

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="rounded-2xl border border-white/[0.08] bg-[#0F1520]/95 backdrop-blur-2xl shadow-2xl overflow-hidden">
        {/* ============ 搜索栏 ============ */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
          <Search size={16} className="text-gray-500 flex-shrink-0" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索应用、工作区、操作..."
            className="flex-1 bg-transparent text-[14px] text-white placeholder-gray-600 outline-none"
          />
          <kbd className="text-[9px] font-mono text-gray-600 px-1.5 py-0.5 rounded border border-white/[0.06] bg-white/[0.02]">ESC</kbd>
        </div>

        {/* ============ 分类标签 ============ */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.06]">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const active = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                  active ? "bg-white/[0.06] text-white" : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.02]"
                }`}
              >
                <Icon size={10} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* ============ 启动项列表 ============ */}
        <div className="max-h-[400px] overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-[12px] text-gray-600">
              未找到匹配项
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {filtered.map((item) => {
                const Icon = item.icon;
                return (
                  <motion.button
                    key={item.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={item.action}
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.04] hover:border-white/[0.08] transition-all text-left"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}
                    >
                      <Icon size={14} style={{ color: item.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-medium text-white truncate">{item.label}</span>
                        {item.external && <ExternalLink size={9} className="text-gray-600" />}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">{item.description}</div>
                    </div>
                    <ArrowRight size={12} className="text-gray-700 group-hover:text-gray-400 transition-colors flex-shrink-0" />
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>

        {/* ============ 底部状态 ============ */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-white/[0.06] bg-white/[0.01]">
          <div className="flex items-center gap-2 text-[9px] font-mono text-gray-600 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            XEAOS AI Launcher v1.0
          </div>
          <div className="text-[9px] font-mono text-gray-600">
            {filtered.length} 项 · {allItems.length} 总计
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 辅助图标
// ============================================

function LayoutGrid({ size, className }: { size: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
